// Lara's coaching breakdown of a quote — every section explains the "why" so AMs learn as they use it.
import { useState } from "react";
import { toast } from "sonner";
import { Copy, ChevronDown, Hammer, Package, TrendingUp, ShieldCheck, HelpCircle, Tags, CalendarClock, MessageSquareWarning, Phone, GraduationCap, BookOpen } from "lucide-react";
import LaraBasic from "./LaraBasic";

const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

function Section({ id, icon: Icon, title, learn, count, children, open: init = true }) {
  const [open, setOpen] = useState(init);
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`lara-section-${id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left" data-testid={`lara-section-toggle-${id}`}>
        <Icon className="w-4 h-4 text-amber-300 shrink-0" />
        <span className="text-[13px] font-semibold text-white flex-1">{title}{count != null && <span className="text-slate-500 font-mono text-[11px]"> · {count}</span>}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3.5 pb-3 space-y-2">
          {learn && <div className="text-[11px] text-teal-300/90 flex gap-1.5"><GraduationCap className="w-3.5 h-3.5 shrink-0 mt-px" /><span><b>Why it matters:</b> {learn}</span></div>}
          {children}
        </div>
      )}
    </section>
  );
}

const Line = ({ label, children }) => children ? <div className="text-[12.5px] text-slate-300"><span className="text-slate-500 text-[10.5px] uppercase tracking-wider font-mono mr-1.5">{label}</span>{children}</div> : null;

function Say({ text: raw, testid }) {
  const text = String(raw || "").trim().replace(/^["“”']+|["“”']+$/g, "");
  if (!text) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-500/[0.07] px-2.5 py-1.5" data-testid={testid}>
      <Phone className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
      <span className="text-[12.5px] text-amber-100 flex-1">“{text}”</span>
      <button type="button" onClick={() => copy(text)} className="text-slate-500 hover:text-white" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function UpsellGroups({ items }) {
  const [by, setBy] = useState("area");
  const sorted = [...items].sort((a, b) => (a.size === "small") - (b.size === "small"));
  const groups = sorted.reduce((m, u) => { const k = (by === "area" ? u.area : u.build_stage) || "Other"; (m[k] = m[k] || []).push(u); return m; }, {});
  return (
    <div className="space-y-2.5" data-testid="lara-upsell-groups">
      <div className="flex gap-1.5">
        {[["area", "By where it's fitted"], ["stage", "By build stage"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setBy(k)} data-testid={`lara-upsell-by-${k}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${by === k ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200" : "border-white/10 text-slate-400"}`}>{l}</button>
        ))}
      </div>
      {Object.entries(groups).map(([g, us]) => (
        <div key={g} className="space-y-1.5">
          <div className="text-[10.5px] uppercase tracking-widest font-mono text-emerald-300/80">{g}</div>
          {us.map((u, i) => (
            <div key={i} className="space-y-1 pl-2 border-l-2 border-emerald-500/30">
              <div className="text-[13px] text-emerald-200 font-semibold flex items-center gap-2">{u.item}
                {u.size === "big" && <span className="px-1.5 rounded bg-emerald-500/20 text-emerald-200 text-[9.5px] font-bold tracking-wider">BIG ITEM</span>}
                {by === "area" && u.build_stage && <span className="text-[10.5px] text-slate-500 font-normal">· {u.build_stage}</span>}
                {by === "stage" && u.area && <span className="text-[10.5px] text-slate-500 font-normal">· {u.area}</span>}
              </div>
              <div className="text-[12.5px] text-slate-300">{u.why}</div>
              <Say text={u.say_this} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function LaraAnalysis({ a, mode = "full" }) {
  const d = a?.data || {};
  const list = (x) => (Array.isArray(x) ? x : []);
  if (mode === "basic") return <LaraBasic d={d} />;
  return (
    <div className="space-y-3" data-testid="lara-analysis">
      <div className="rounded-xl border border-violet-400/30 bg-violet-500/[0.08] p-3.5">
        <div className="flex items-start gap-3">
          <div className="text-[13.5px] text-white font-medium flex-1" data-testid="lara-headline">{d.headline}</div>
          {d.win_score != null && (
            <div className="text-center shrink-0" title="Lara's read of win likelihood with what we know now" data-testid="lara-win-score">
              <div className="text-2xl font-bold text-violet-200 tabular-nums">{d.win_score}</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">win %</div>
            </div>
          )}
        </div>
        {d.build && <div className="mt-2 text-[12.5px] text-slate-300"><b className="text-slate-100">{d.build.type}</b>{d.build.phase ? ` · ${d.build.phase}` : ""}<div className="mt-1">{d.build.summary}</div></div>}
      </div>

      {d.call_plan && (
        <Section id="call-plan" icon={Phone} title="Call plan" learn="Lead with their project, not the price — confidence comes from knowing what they're building.">
          <Say text={d.call_plan.opener} testid="lara-opener" />
          {list(d.call_plan.value_points).map((v, i) => <div key={i} className="text-[12.5px] text-slate-300 pl-1">• {v}</div>)}
          <Say text={d.call_plan.close} testid="lara-close" />
        </Section>
      )}

      <Section id="questions" icon={HelpCircle} title="Key questions to ask" count={list(d.key_questions).length} learn="Good questions uncover the full job — and the next order.">
        {list(d.key_questions).map((q, i) => (
          <div key={i} className="flex items-start gap-2 text-[12.5px] text-slate-200"><span className="text-violet-300 font-mono text-[11px] mt-px">{i + 1}</span><span className="flex-1">{q}</span>
            <button type="button" onClick={() => copy(q)} className="text-slate-500 hover:text-white" title="Copy"><Copy className="w-3.5 h-3.5" /></button></div>
        ))}
      </Section>

      <Section id="products" icon={Package} title="Products — what, where & how they're fitted" count={list(d.products).length} open={false}
        learn="Knowing how each item is installed lets you spot missing parts and sound like the expert.">
        {list(d.products).map((p, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] p-2.5 space-y-1">
            <div className="text-[13px] text-white font-semibold">{p.name}</div>
            <Line label="What">{p.what_it_is}</Line><Line label="Used for">{p.used_for}</Line>
            <Line label="Fitted">{p.how_installed}</Line><Line label="Where">{p.where_fitted}</Line>
            {p.watch_outs && <div className="text-[12px] text-rose-300/90">⚠ {p.watch_outs}</div>}
          </div>
        ))}
      </Section>

      <Section id="upsells" icon={TrendingUp} title="Upsell & cross-sell" count={list(d.upsells).length} learn="Go for the big items for this area and the NEXT stage of the build — that's how one quote becomes a long-term customer.">
        <UpsellGroups items={list(d.upsells)} />
      </Section>

      <Section id="regs" icon={ShieldCheck} title="Building regs to check" count={list(d.building_regs).length} open={false} learn="Regs drive spec. Asking about Building Control builds trust and avoids wrong products.">
        {list(d.building_regs).map((r, i) => (
          <div key={i} className="text-[12.5px] text-slate-300"><b className="text-sky-200">{r.reg}</b> — {r.why_it_matters}{r.ask_or_check && <div className="text-slate-400 mt-0.5">Ask: {r.ask_or_check}</div>}</div>
        ))}
      </Section>

      <Section id="next-phase" icon={CalendarClock} title="Next phase — follow-up plan" count={list(d.next_phase).length} open={false} learn="Every build has a next stage. Book the follow-up now and own the whole project.">
        {list(d.next_phase).map((n, i) => (
          <div key={i} className="text-[12.5px] text-slate-300"><b className="text-white">{n.phase}</b> — {n.products}{n.when_to_follow_up && <div className="text-amber-200/90">Follow up: {n.when_to_follow_up}</div>}</div>
        ))}
      </Section>

      <Section id="objections" icon={MessageSquareWarning} title="Objections & answers" count={list(d.objections).length} open={false} learn="Have the answer ready before they ask — that's what sounds confident.">
        {list(d.objections).map((o, i) => (
          <div key={i} className="text-[12.5px]"><div className="text-rose-200">“{o.objection}”</div><div className="text-slate-300 mt-0.5">→ {o.answer}</div></div>
        ))}
      </Section>

      <Section id="keywords" icon={Tags} title="Words to use" count={list(d.keywords).length} open={false}>
        <div className="flex flex-wrap gap-1.5">{list(d.keywords).map((k, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[11.5px] border border-white/10 text-slate-200">{k}</span>)}</div>
      </Section>

      <Section id="coach" icon={GraduationCap} title="Coach's corner — principles" count={list(d.coach_tips).length} open={false} learn="Principles you can reuse on every quote. Hub links show where to read more.">
        {list(d.coach_tips).map((c, i) => (
          <div key={i} className="text-[12.5px]"><b className="text-teal-200">{c.principle}</b><div className="text-slate-300">{c.how_to_apply}</div>{c.hub_ref && <div className="text-[11px] text-slate-500 flex items-center gap-1"><BookOpen className="w-3 h-3" /> Hub: {c.hub_ref}</div>}</div>
        ))}
      </Section>

      {(list(d.sources).length > 0 || list(a?.references).length > 0) && (
        <div className="text-[11px] text-slate-500" data-testid="lara-sources"><Hammer className="w-3 h-3 inline mr-1" />Based on: {(list(d.sources).length ? d.sources : a.references).join(" · ")}</div>
      )}
    </div>
  );
}
